import { Alert, Box, Link, SxProps, Theme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";

interface RevisionSubmission {
  id: string;
  title: string | null;
  createdAt?: string;
}

interface SubmissionRevisionBannerProps {
  tenantId: string;
  revisedFromSubmission?: { id: string; title: string | null; createdAt?: string } | null;
  revisionSubmissions?: RevisionSubmission[];
  /** Override the base path for links (e.g. "/staff/fulfillment/waste/submissions") */
  basePath?: string;
  sx?: SxProps<Theme>;
}

export function SubmissionRevisionBanner({
  tenantId,
  revisedFromSubmission,
  revisionSubmissions,
  basePath,
  sx,
}: SubmissionRevisionBannerProps) {
  const hasParent = !!revisedFromSubmission;
  const hasRevisions = !!revisionSubmissions?.length;

  if (!hasParent && !hasRevisions) {
    return null;
  }

  const buildLink = (submissionId: string) => {
    if (basePath) {
      return `${basePath}/${submissionId}`;
    }
    return `/o/${tenantId}/waste/submissions/${submissionId}`;
  };

  return (
    <>
      {hasParent && (
        <Alert severity="info" sx={sx}>
          This is a revision of{" "}
          <Link
            component={RouterLink}
            to={buildLink(revisedFromSubmission.id)}
            target="_blank"
            rel="noopener"
          >
            {revisedFromSubmission.title ?? "Untitled Submission"}
          </Link>
        </Alert>
      )}
      {hasRevisions && (
        <Alert severity="info" sx={sx}>
          This submission has revisions:
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
            {revisionSubmissions.map((rev) => (
              <li key={rev.id}>
                <Link component={RouterLink} to={buildLink(rev.id)} target="_blank" rel="noopener">
                  {rev.title ?? "Untitled Submission"}
                </Link>
                {rev.createdAt ? ` — Created ${prettyPrintDateMed(rev.createdAt)}` : ""}
              </li>
            ))}
          </Box>
        </Alert>
      )}
    </>
  );
}
